import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, Permit } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['IMMIGRATION_OFFICER', 'SENIOR_IO'];
const SENIOR_ROLES = ['SENIOR_IO'];

/** Immigration — issued permit register (M03): status, validity, and senior-only revocation. */
export function Permits() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const isSenior = persona ? persona.roles.some((r) => SENIOR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/residency/permits?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<Permit>>(path);

  return (
    <div>
      <PageHeader
        title="Permits"
        subtitle="Issued residence/permanent permits — status and validity window (M03)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-52" placeholder="Serial, NIN, passport, or name…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
              <option value="REVOKED">Revoked</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Serial</th>
                <th className="table-head">Holder</th>
                <th className="table-head">Type</th>
                <th className="table-head">Valid to</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.serial}</td>
                  <td className="table-cell">
                    {p.fullName}
                    <div className="font-mono text-[11px] text-slate-400">{p.nin ?? p.passportNo}</div>
                  </td>
                  <td className="table-cell">{p.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(p.validTo)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <PermitDetailModal
          id={selectedId}
          isSenior={isSenior}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({
  id,
  isSenior,
  onClose,
  onChanged,
}: {
  id: string;
  isSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const permitQ = useApiQuery<Permit>(`/api/v1/residency/permits/${id}`);
  const p = permitQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRevoke, setShowRevoke] = useState(false);
  const [reason, setReason] = useState('');

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/residency/permits/${id}/revoke`, { reason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to revoke');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={p ? `${p.serial} — ${p.fullName}` : 'Permit'} onClose={onClose}>
      {permitQ.error ? <ErrorNote message={permitQ.error} /> : null}
      {permitQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Type" value={p.type.replaceAll('_', ' ')} />
            <Detail label="Identity" value={p.nin ?? `PP:${p.passportNo}`} mono />
            <Detail label="Nationality" value={p.nationality} />
            <Detail label="Valid from" value={formatDate(p.validFrom)} />
            <Detail label="Valid to" value={formatDate(p.validTo)} />
            <Detail label="Conditions" value={p.conditions.length ? p.conditions.join(', ') : '—'} />
            {p.cardDocVaultRef ? <Detail label="DocVault card ref" value={p.cardDocVaultRef} mono /> : null}
          </div>

          {p.status === 'REVOKED' && p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {isSenior && p.status === 'ACTIVE' ? (
            !showRevoke ? (
              <button className="btn-danger" onClick={() => setShowRevoke(true)}>
                Revoke permit
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <Field label="Revocation reason">
                  <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <button className="btn-secondary" onClick={() => setShowRevoke(false)}>
                    Cancel
                  </button>
                  <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={revoke}>
                    {busy ? 'Revoking…' : 'Confirm revoke'}
                  </button>
                </div>
              </div>
            )
          ) : !isSenior && p.status === 'ACTIVE' ? (
            <RoleNotice roles={SENIOR_ROLES} />
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Issued {formatDateTime(p.createdAt)} · last updated {formatDateTime(p.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
